import { useEffect, useState } from 'react';

/** The codebase's mobile breakpoint, matching the dominant CSS media query. */
export const MOBILE_BREAKPOINT = 768;

/**
 * Track whether the viewport is at or below the mobile breakpoint.
 *
 * Uses matchMedia rather than a resize listener: it fires only when the boundary
 * is actually crossed, so a live-filtering page is not re-rendering on every
 * pixel of a window drag.
 *
 * Note this is distinct from useWindowSize, whose default breakpoint is 480 and
 * which exists to report dimensions rather than answer this one question.
 */
export const useIsMobile = (breakpoint: number = MOBILE_BREAKPOINT): boolean => {
  const query = `(max-width: ${breakpoint}px)`;

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    // Re-sync in case the viewport changed between render and effect.
    setIsMobile(mql.matches);

    // addListener is the Safari <14 spelling; still needed for older iOS.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isMobile;
};

export default useIsMobile;
